import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../css/Work.css';
import { pad, useActiveSection, SectionIndex, Plate } from './Comp_Editorial.js';

const img = (slug, file) => `${process.env.PUBLIC_URL}/work/${slug}/${file}`;

const ALL_PROJECTS = [
  {
    slug: 'wovenworlds',
    title: 'WovenWorlds',
    client: 'Personal project',
    kind: 'AI-narrated multiplayer story game',
    tagline: 'Story roleplay with an AI game master. Every choice leaves a thread.',
    cover: img('wovenworlds', 'cover.jpg'),
    opening: {
      src: img('wovenworlds', 'story-view-mockup.mp4'),
      poster: img('wovenworlds', 'story-view-mockup-poster.jpg'),
      video: true,
      caption: 'Story view mockup',
    },
    galleryLabel: 'Mockups',
    link: 'https://wovenworld.io/',
    summary:
      'WovenWorlds is a story game where players build a world, create their characters, and play it out turn by turn. An AI game master narrates, voices every other character, and keeps a running record of the story, in any genre, solo or with friends.',
    facts: [
      ['Role', 'Independently designed and built, from UI/UX through infrastructure'],
      ['Stack', 'Local LLM deployment, custom prompt architecture, and real-time multiplayer on self-hosted hardware'],
      ['Tools', 'Figma for UI/UX design and Claude Code to accelerate development'],
      ['Status', 'Closed alpha, invite-only while in testing'],
    ],
    story: [
      {
        label: 'Challenge',
        title: 'A consistent story.',
        text: 'An AI storyteller has to stay consistent with the world the player built, remember what already happened, and keep several players in sync, all without the player managing the model by hand.',
        gallery: [
          {
            src: img('wovenworlds', 'preset-library.jpg'),
            caption: 'Preset Library',
            desc: 'Players start from a community scenario or one of their own. Picking a preset fills in World Building’s prompts and lore, ready to tweak before the first message.',
          },
          {
            src: img('wovenworlds', 'narrative-style.jpg'),
            caption: 'World Building: Narrative Style',
            desc: 'Tune how the game master tells the story: dialogue style, difficulty, and how often dice-driven complications get forced into a turn.',
          },
          {
            src: img('wovenworlds', 'map-editor.jpg'),
            caption: 'Map Editor',
            desc: 'Territory authoring for a campaign: import or export map data, paint provinces by nation, add places, and set the map’s scale.',
          },
          {
            src: img('wovenworlds', 'story-book-map.jpg'),
            caption: 'Story Book with live map',
            desc: 'The play view in split screen: narration and attributed character dialogue beside an interactive territory map that follows the story.',
          },
          {
            src: img('wovenworlds', 'connection-settings.jpg'),
            caption: 'Model connection settings',
            desc: 'Bring your own model: connect Anthropic, OpenAI, Gemini, or OpenRouter with your own key, or point the game master at a local model server.',
            wide: true,
          },
        ],
      },
      {
        label: 'Approach',
        title: 'Context in every turn.',
        text: 'The world’s rules, characters, and lore are written once and sent to the game master with every turn alongside the story so far. A custom prompt architecture turns scene markup, speaker attribution, and suggested actions into structured play, and players can bring their own model or run it against a local server.',
        blueprint: {
          title: 'Experience blueprint',
          thesis: {
            items: [
              ['The players', 'Curious solo players, game masters, and world creators who want authored structure without a fixed script.'],
              ['The challenge', 'Generative stories can feel unmoored: prose forgets context, maps become decoration, and configuration sits apart from play.'],
              ['The solution', 'Unify campaign memory, narrative behavior, character dialogue, and a living territory map in one coherent game loop.'],
              ['The design promise', 'Every choice should feel situated in a world with names, borders, loyalties, history, and consequences that persist.'],
            ],
            tags: ['Player-shaped', 'World-aware', 'Legible control', 'Persistent context'],
            quote: 'A storyteller with a memory, a map, and room for surprise.',
          },
          systems: [
            ['Campaign management', 'Create, revisit, and move among persistent story worlds.'],
            ['Narrative play', 'Character dialogue and prose stay live beside the territory map.'],
            ['Cartographic engine', 'Import and edit GeoJSON; name regions, factions, and territory.'],
            ['World context', 'World, character, and lore context ground every response.'],
            ['Story direction', 'Tune voice, difficulty, complication, pacing, and creativity.'],
            ['Model connection', 'Choose a provider or local server, credentials, and model.'],
          ],
          journey: [
            ['Find a premise', 'Search and filter a community scenario, or start blank.'],
            ['Build the world', 'Set prompts, lore, characters, regions, and factions.'],
            ['Tune the voice', 'Direct style, risk, complications, and creativity.'],
            ['Connect a model', 'Select a provider, local server, and model.'],
            ['Shape the story', 'Choose, speak, and watch context and territory evolve.'],
          ],
          loop: ['Input a choice', 'Generate a story moment', 'Update world context', 'Reveal new choices'],
          customization: [
            ['World source', 'Community preset / From scratch'],
            ['Map layer', 'Import GeoJSON · Edit territories'],
            ['Narrative', 'Style · Difficulty · Creativity'],
            ['Intelligence', 'Provider · Server · Model'],
          ],
          community: {
            text: 'A searchable, filterable preset library turns finished worlds into reusable starting points. Players can browse scenarios, inspect their premise, and carry the setup into World Building for review and revision.',
            chips: ['Search', 'Genre', 'Content', 'Sort', 'Remix'],
          },
          visual: {
            text: 'A mythic cartographic command center: deep near-black planes, fine ruled panels, compact controls, restrained violet focus, terrain texture, and warm story highlights.',
            swatches: [['Void', '#08080d'], ['Panel', '#1d1b28'], ['Focus', '#bfa8ea'], ['Story', '#d3a85c'], ['Ink', '#f2eee8']],
          },
        },
      },
      {
        label: 'Outcome',
        title: 'A living world.',
        text: 'A fully functional, playable game: a living world where a Lore Till Now chronicle and character development logs update as in-story time passes, and friends can join a campaign with an invite code.',
      },
    ],
    capabilities: [
      ['World building', 'Setting, history, factions, tone, difficulty, and major events, from scratch or a community preset.'],
      ['AI game master', 'Narrates results, voices every other character, and keeps the story consistent with the world’s rules.'],
      ['Bring your own model', 'Connect Anthropic, OpenAI, Gemini, OpenRouter, or a locally hosted model.'],
      ['Play together', 'Each player submits an action or passes, and the host sends the round to the GM.'],
    ],
    images: [
      {
        src: img('wovenworlds', 'landing-page-full.jpg'),
        caption: 'wovenworld.io landing page',
        desc: 'The complete public site, top to bottom: the hero, how it works, a sample story turn, genre examples, and the invite-only sign-up. Scroll to see the whole page.',
        tall: true,
      },
      {
        src: img('wovenworlds', 'mobile-mockup.mp4'),
        poster: img('wovenworlds', 'mobile-mockup-poster.jpg'),
        video: true,
        caption: 'Mobile mockup',
        desc: 'The full journey on a phone, from the first thread to a world of your own: the same landing page, scrolled top to bottom on a mobile layout.',
      },
    ],
  },
  {
    slug: 'vodpro',
    title: 'VODPRO',
    client: 'Product Operations',
    kind: 'Internal platform',
    tagline: 'A shared operating picture for product delivery.',
    cover: img('vodpro', 'vodpro-dashboard.png'),
    summary:
      'VODPRO consolidates portfolio health, team load, owner actions, and source-control activity into a focused daily view for product and engineering leaders.',
    facts: [
      ['Role', 'Designed and built it entirely on my own, a one-person team taking it from 0 to 1 for internal stakeholders'],
      ['Users', 'Product owners, portfolio leads, and engineering managers'],
      ['Signals', 'Jira delivery data, Monday.com, Confluence, Slack, and GitHub activity'],
    ],
    story: [
      {
        label: 'Challenge',
        title: 'Too many systems, no single picture.',
        image: { src: img('vodpro', 'challenge-ticket-structure.jpg'), caption: 'A single project structure', note: 'One ticket template for every new project' },
        text: 'Project, product, and engineering managers struggled to stay informed on where things actually stood. Like most large companies, the organization held licenses to dozens of project planning systems, with Jira as the primary one, so status and blockers were scattered across tools and every team structured its work differently. The goal of VODPRO was one project structure for all new projects, so any project’s status and potential blockers could be tracked at any given time.',
      },
      {
        label: 'Approach',
        title: 'A report built for the PM.',
        image: { src: img('vodpro', 'vodpro-todos.png'), caption: 'Owner to-dos and daily briefing', note: 'Jira · Monday.com · Confluence · Slack' },
        text: 'A modular, report-like system pairs executive summaries with drill-down tables, status-led cards, and a concise daily briefing. The same visual grammar repeats across every view, so each one feels familiar, dense, and fast to scan. I also built a report specifically for me as the product manager, focused on what was mine to follow up on. It pulled action items from Jira, Monday.com, Confluence, and Slack into one list, and added a daily digest of what coworkers had published on Confluence that I should read and catch up on, which kept me in touch with work well outside my direct scope.',
      },
      { label: 'Outcome', title: 'Consistent status.', text: 'A coherent operating surface that helps teams inspect delivery state, spot what needs attention, and move from portfolio context to source activity without replacing the systems of record.' },
    ],
    capabilities: [
      ['Portfolio health', 'Progress, phases, blockers, open tickets, and workstream status.'],
      ['Capacity auditing', 'Active story points compared against team and individual sprint thresholds.'],
      ['Owner briefing', 'Today, this week, blocked decisions, and capacity exceptions, prioritized.'],
      ['GitHub reporting', 'Repository activity and Jira-linked delivery traceability.'],
    ],
    images: [
      { src: img('vodpro', 'vodpro-product-operations-design-brief.png'), caption: 'Product design brief' },
      { src: img('vodpro', 'vodpro-dashboard.png'), caption: 'Portfolio and workstream health' },
      { src: img('vodpro', 'vodpro-capacity.png'), caption: 'Team capacity audit' },
      { src: img('vodpro', 'vodpro-github.png'), caption: 'GitHub activity reporting' },
    ],
  },
  {
    slug: 'vut',
    title: 'Media Uploader',
    client: 'NBCUniversal',
    kind: 'Video Upload Tool (VUT)',
    tagline: 'A controlled path from source media to playable delivery.',
    cover: img('vut', 'jobs-list.png'),
    summary:
      'An operational tool for selecting video and imagery, enriching assets with structured metadata, and monitoring automated processing through inspection and playback. Built for a limited shortform use case, it grew into the primary metadata enrichment tool for NOW TV in Italy, Germany, Australia, and the UK.',
    facts: [
      ['Role', 'Helped design, build, and deploy the platform'],
      ['Content', 'Primarily shortform (SF) media: promos, clips, and trailers'],
      ['Regions', 'NOW TV in Italy, Germany, Australia, and the UK'],
      ['Users', 'Editorial operators, media operations, and distribution teams'],
      ['Focus', 'Ingest, metadata enrichment, processing queues, inspection, and playback'],
    ],
    story: [
      {
        label: 'Challenge',
        title: 'Fast, but exact.',
        image: { src: img('vut', 'create-upload.png'), caption: 'Create a video upload', note: 'Video · Images · Metadata · Schedule', light: true },
        text: 'Shortform content needed a fast, reliable way in for one limited scenario. Operators had to provide precise editorial, territorial, scheduling, and technical context while keeping long-running processing visible and errors inspectable.',
      },
      {
        label: 'Approach',
        title: 'Create, monitor, inspect.',
        image: { src: img('vut', 'job-details.png'), caption: 'Job details and manifests', note: 'Summary · Video/audio · HLS/DASH', light: true },
        text: 'A guided upload form feeds deterministic queues. Filterable job views and asset details make every state, field, and output available for human verification. Create, then Jobs, then Job detail: progressive disclosure keeps high-volume monitoring fast while preserving field-level evidence.',
      },
      {
        label: 'Outcome',
        title: 'From pilot to primary tool.',
        text: 'What started as a tool for a single limited use case expanded into the primary metadata enrichment tool for NOW TV across Italy, Germany, Australia, and the UK, making shortform intake, processing state, technical inspection, and playback review coherent in one workflow.',
      },
    ],
    capabilities: [
      ['Select + enrich', 'Choose video and image assets, then add structured editorial and distribution metadata.'],
      ['Territory + schedule', 'Make availability constraints explicit before processing begins.'],
      ['Process + monitor', 'Automated queue stages stay visible through filters, status, timing, and job inspection.'],
      ['Inspect + play', 'Review technical video and audio metadata and open playable HLS and DASH manifests.'],
    ],
    images: [
      {
        src: img('vut', 'media-uploader-design-brief.png'),
        caption: 'Product design brief',
        desc: 'The one-page case study: project scope, the four core views, purpose and design response, core capabilities, workflow, and the pre-AI operating model.',
        wide: true,
        light: true,
      },
      {
        src: img('vut', 'jobs-list.png'),
        caption: 'Jobs list and status monitoring',
        desc: 'Every upload in one filterable list, searchable by genre, territory, and status, with created, processing, completed, and error states and processing times at a glance.',
        light: true,
      },
      {
        src: img('vut', 'queues.png'),
        caption: 'Processing queues',
        desc: 'The automated ingest, transcode, and package stages, showing what is waiting, what has processed, and what failed.',
        wide: true,
        light: true,
      },
    ],
  },
  // Draft: shows on localhost only. The production build drops this entry entirely.
  ...(process.env.NODE_ENV === 'development' ? [
    {
      slug: 'vodpipeline',
      title: 'VOD Pipeline Map',
      client: 'NBCUniversal',
      kind: 'Systems analysis and architecture',
      tagline: 'Dozens of team diagrams, rebuilt as one picture of how a title reaches the screen.',
      cover: require('../drafts/vodpipeline/cover.svg').default,
      summary:
        'An end-to-end model of the video-on-demand supply chain, from partner delivery through ingest, encoding, packaging, metadata, and publishing to playback. I built it by reconciling the diagrams, runbooks, and working knowledge of every team that touched a title. The version shown here is generalized so it can be shared.',
      facts: [
        ['Role', 'Researched, modeled, and maintained the end-to-end workflow'],
        ['Scope', 'Content supply, media processing, metadata and rights, publishing, and playback'],
        ['Audience', 'Product, engineering, architecture, and program leads'],
        ['Note', 'Systems and teams are generalized. Vendor tools appear by role, such as Encoder A and Encoder B.'],
      ],
      story: [
        {
          label: 'Challenge',
          title: 'No single map.',
          image: { src: require('../drafts/vodpipeline/sources.svg').default, caption: 'Where it started', note: 'Dozens of sources · No shared view' },
          text: 'Each team documented its own corner of the pipeline in its own way. The diagrams overlapped, contradicted each other, and stopped at team boundaries, which is where most delivery problems actually lived. Planning any cross-team work meant rebuilding the picture from scratch.',
        },
        {
          label: 'Approach',
          title: 'Reconcile, then simplify.',
          image: { src: require('../drafts/vodpipeline/stage-anatomy.svg').default, caption: 'Anatomy of a stage', note: 'Inputs · Systems · Owner · Handoffs' },
          text: 'I gathered every source diagram I could find, walked the flow with the teams that owned each step, and rebuilt it as one workflow organized by stage instead of by org chart. Every stage records the same things: inputs, systems, owner, outputs, and handoffs. That made gaps, duplicated work, and slow handoffs visible where the stages meet.',
        },
        {
          label: 'Outcome',
          title: 'A shared reference.',
          text: 'The map became the starting point for cross-team planning. Teams used it to scope new initiatives, trace issues back to the stage that caused them, and spot places where two systems were doing the same job.',
        },
      ],
      capabilities: [
        ['Stages', 'The path a title takes, in order, from partner delivery to the player.'],
        ['Systems', 'What runs each step, named by role so the model outlasts any one vendor.'],
        ['Owners', 'Which team is accountable at each step, and where nobody clearly is.'],
        ['Handoffs', 'Where work crosses team lines, which is where delay and risk tended to collect.'],
      ],
      images: [
        { src: require('../drafts/vodpipeline/cover.svg').default, caption: 'End-to-end VOD workflow, generalized' },
      ],
    },
  ] : []),
  {
    slug: 'cerner',
    title: 'Value Tracker',
    client: 'Cerner Corporation',
    kind: 'Test case tracking and reporting',
    tagline: 'One place to plan, run, and report on testing for Cerner’s new features and internal tools.',
    cover: img('cerner', 'home.png'),
    summary:
      'Value Tracker gave Cerner’s internal teams one place to track test cases for new features and internal tools: organizing them into test projects, following automated and manual runs, and recording results. Clients could also see the test cases behind their work and pull reports on the results.',
    facts: [
      ['Role', 'Designed the platform and helped build it; as the team’s manager, oversaw most of the project'],
      ['Users', 'Internal product, QA, and engineering teams, plus clients with visibility into test cases and reports'],
      ['Scope', 'Test projects, a searchable test case library, automated and manual runs, results entry, and dashboards'],
    ],
    story: [
      {
        label: 'Challenge',
        title: 'Testing, team by team.',
        image: { src: img('cerner', 'project-search.png'), caption: 'Test project search', note: 'Tool · Owner · Environment · Next run', light: true },
        text: 'Testing for new features and internal tools was tracked team by team, so it was hard to see what was covered, what had run, and what was failing, and harder still to share that status with clients.',
      },
      {
        label: 'Approach',
        title: 'One flow, one set of filters.',
        image: { src: img('cerner', 'test-dashboard.png'), caption: 'Test dashboard', note: 'Coverage · Pass trend · Needs attention', light: true },
        text: 'I designed a single flow from test project to test run to test case to result, with the same “Modify your view” filters on every screen so teams always knew where they were. Dashboards surfaced pass trends, automation coverage, and the failures that needed attention, and new test cases went through a request and Test Admin review step. I led the team that built it and oversaw the project through delivery.',
      },
      {
        label: 'Outcome',
        title: 'Coverage in plain sight.',
        text: 'Internal teams could see coverage, pass rates, and blocked or failing tests in one place, and clients could review test cases and pull reports without waiting on the team for status updates.',
      },
    ],
    capabilities: [
      ['Project search', 'Find any test project by tool, owner, environment, or date, and see its next scheduled run.'],
      ['Test case library', 'Search every test case by feature area, method, frequency, and result, or request a new one.'],
      ['Test dashboard', 'Pass trends, automation coverage, status breakdowns, and the failures that need attention.'],
      ['Results + reporting', 'Record expected versus actual results and failure reasons, then export summaries for clients.'],
    ],
    images: [
      {
        src: img('cerner', 'style-brief.png'),
        caption: 'Product design brief',
        note: 'Style brief: palette, type, shell, surfaces, forms, tables and charts',
        desc: 'The visual system behind all five screens: a white canvas with restrained blue accents, compact type, a shared header and navigation rail, lightly bordered modules, and dense tables and charts.',
        wide: true,
        light: true,
      },
      {
        src: img('cerner', 'home.png'),
        caption: 'Home',
        desc: 'A personal starting point: active projects and test cases, the latest pass rate, recent projects, what needs attention, and recent activity.',
        light: true,
      },
      {
        src: img('cerner', 'search-test-cases.png'),
        caption: 'Search test cases',
        desc: 'The full test case library, searchable by feature area, environment, execution method and frequency, status, and latest result, with a shortcut to request a new case.',
        light: true,
      },
      {
        src: img('cerner', 'test-results.png'),
        caption: 'Test results',
        desc: 'Results entry for a single run: expected versus actual results, failure reasons, and pass, fail, or blocked status for every test case.',
        light: true,
      },
    ],
  },
  {
    slug: 'kceventhub',
    hidden: true, // retired: the original site is no longer in operation
    title: 'KCEventHub.org',
    client: 'City of Kansas City, MO',
    kind: 'Event permitting platform',
    tagline: 'Kansas City’s paper permitting process, digitized end to end.',
    cover: img('kceventhub', 'cover.png'),
    link: 'http://kceventhub.org/',
    summary:
      'Kansas City used a complex paper system to process every event in the metro area. Since March 2015 the entire permitting process has been digital. KCEventHub guides organizers through the outdoor event application and provides the guidelines and requirements for special event management in the city.',
    facts: [
      ['Role', 'Lead developer, front end to back end, database infrastructure, and server security'],
      ['Stack', 'WordPress, a custom calendar plugin, and Gravity Forms with custom permitting logic'],
      ['Impact', '1,500 events registered and 6,000 forms completed as of April 2022'],
    ],
    story: [
      { label: 'Challenge', text: 'An event can need many permits depending on its location, size, and activities, and those rules come from city ordinance, local law, and individual departments. Organizers needed one place to start and a clear path to everything else they had to file.' },
      { label: 'Approach', text: 'An organizer fills out a main application with their event details. Business logic built on city ordinances, regulations, and local law then routes them to every other application and form their event requires. The site also pulls in calendars from other Kansas City sites, government agencies, and partners to show everything happening around the city.' },
      { label: 'Planning', text: 'A first-draft BPMN mapped the MVP: two main components and three user types, each needing access in different ways. It traced every path from a user’s first visit, through each option and notification, to the end goal.' },
      { label: 'Outcome', text: 'In 2020 the older KCRaceDay site was folded into KCEventHub, so every event type in the metro can now be registered in one place.' },
    ],
    images: [
      { src: img('kceventhub', 'cover.png'), caption: 'Responsive site across desktop, tablet, and phone', wide: true },
      { src: img('kceventhub', 'calendar.png'), caption: 'Citywide event calendar', wide: true },
      { src: img('kceventhub', 'permits.png'), caption: 'Organizer dashboard: required permits for an event' },
      { src: img('kceventhub', 'bpmn.png'), caption: 'First-draft BPMN of the MVP permitting flow', wide: true },
    ],
  },
];

// Projects flagged hidden stay in the code but never appear on the site.
const PROJECTS = ALL_PROJECTS.filter((p) => !p.hidden);

function Lightbox({ images, index, onClose, onStep }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);

  const image = images[index];
  return (
    <div className="pb-lightbox" onClick={onClose}>
      {image.video ? (
        <video key={image.src} src={image.src} poster={image.poster} aria-label={image.caption} controls autoPlay muted loop playsInline onClick={(e) => e.stopPropagation()} />
      ) : image.tall ? (
        <div className="pb-lightbox-scroll" onClick={(e) => e.stopPropagation()}>
          <img src={image.src} alt={image.caption} />
        </div>
      ) : (
        <img className={image.light ? 'light' : undefined} src={image.src} alt={image.caption} onClick={(e) => e.stopPropagation()} />
      )}
      <p className="pb-lightbox-caption">
        {image.caption} <span>{index + 1} / {images.length}</span>
      </p>
      {image.desc && <p className="pb-lightbox-desc">{image.desc}</p>}
      {images.length > 1 && (
        <>
          <button className="pb-lightbox-nav prev" aria-label="Previous image" onClick={(e) => { e.stopPropagation(); onStep(-1); }}>&#8249;</button>
          <button className="pb-lightbox-nav next" aria-label="Next image" onClick={(e) => { e.stopPropagation(); onStep(1); }}>&#8250;</button>
        </>
      )}
      <button className="pb-lightbox-close" aria-label="Close" onClick={onClose}>&times;</button>
    </div>
  );
}

function WorkIndex() {
  return (
    <main className="pb-folio">
      <div className="pb-folio-intro">
        <div className="pb-folio-title">
          <p className="pb-folio-eyebrow">The portfolio / 01—{pad(PROJECTS.length)}</p>
          <h1>My <em>work,</em><br />in context.</h1>
        </div>
        <div className="pb-folio-lede">
          <p>Platforms, tools, and products I have designed, built, and shipped, from AI storytelling to media operations.</p>
          <p className="pb-folio-accent">A selection of projects, each with its own story.</p>
        </div>
      </div>

      <ol className="pb-projects">
        {PROJECTS.map((p, i) => {
          const num = pad(i + 1);
          const to = `/works/${p.slug}`;
          return (
            <li className={`pb-project fade-in${i % 2 ? ' flip' : ''}`} key={p.slug}>
              <figure className="pb-project-figure">
                <Link to={to} className="pb-project-preview" tabIndex={-1} aria-hidden="true">
                  <img src={p.cover} alt="" loading="lazy" />
                </Link>
                <figcaption>
                  <span>Fig. {num} / {p.kind}</span>
                  <span>{num}</span>
                </figcaption>
              </figure>

              <div className="pb-project-details">
                <p className="pb-project-num">{num} /</p>
                <div className="pb-project-info">
                  <p className="pb-project-client">{p.client}</p>
                  <h2>{p.title}</h2>
                  <p className="pb-project-tagline">{p.tagline}</p>
                </div>
                <span className="pb-project-rule" />
                <Link to={to} className="pb-project-link">
                  View case study <span aria-hidden="true">↗&#xFE0E;</span>
                </Link>
              </div>
            </li>
          );
        })}
      </ol>
    </main>
  );
}

// Content gaps show as dashed "Needed" boxes on localhost only; the
// production build leaves those slots out.
const SHOW_GAPS = process.env.NODE_ENV === 'development';

const NUMBER_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight'];
const BRIEF_CAPTION = 'Product design brief';
const toId = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function Needed({ children, image }) {
  if (!SHOW_GAPS) return null;
  return <div className={`pb-needed${image ? ' image' : ''}`}>Needed: {children}</div>;
}

function GalleryGrid({ images, openPlate }) {
  return (
    <div className="pb-gallery-grid">
      {images.map((im) => (
        <div className={`pb-gallery-item${im.wide ? ' wide' : ''}`} key={im.src}>
          <Plate image={im} left={im.caption} onOpen={openPlate(im)} />
          {im.desc && <p className="pb-gallery-desc">{im.desc}</p>}
        </div>
      ))}
    </div>
  );
}

// An experience blueprint rendered as live page elements instead of a flat image
function Blueprint({ data }) {
  const { thesis, systems, journey, loop, customization, community, visual } = data;
  return (
    <div className="pb-blueprint">
      <p className="pb-case-label">{data.title}</p>

      <div className="pb-bp-block">
        <p className="pb-bp-label"><span>A</span> Experience thesis</p>
        <div className="pb-bp-thesis">
          {thesis.items.map(([title, text]) => (
            <div key={title}>
              <h4>{title}</h4>
              <p>{text}</p>
            </div>
          ))}
        </div>
        <ul className="pb-bp-chips">
          {thesis.tags.map((t) => <li key={t}>{t}</li>)}
        </ul>
        <p className="pb-bp-quote">{thesis.quote}</p>
      </div>

      <div className="pb-bp-block">
        <p className="pb-bp-label"><span>B</span> Core systems</p>
        <ul className="pb-bp-cards">
          {systems.map(([title, text]) => (
            <li key={title}>
              <h4>{title}</h4>
              <p>{text}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="pb-bp-block">
        <p className="pb-bp-label"><span>C</span> Primary journey</p>
        <ol className="pb-bp-journey">
          {journey.map(([title, text], i) => (
            <li key={title}>
              <span className="pb-bp-num">{pad(i + 1)}</span>
              <h4>{title}</h4>
              <p>{text}</p>
            </li>
          ))}
        </ol>
        <p className="pb-bp-loop">
          <span className="pb-bp-num">Play loop</span>
          {loop.join(' → ')}
        </p>
      </div>

      <div className="pb-bp-split">
        <div className="pb-bp-block">
          <p className="pb-bp-label"><span>D</span> Customization model</p>
          <dl className="pb-bp-settings">
            {customization.map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
          </dl>
        </div>

        <div className="pb-bp-block">
          <p className="pb-bp-label"><span>E</span> Community layer</p>
          <p className="pb-bp-text">{community.text}</p>
          <ul className="pb-bp-chips">
            {community.chips.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </div>
      </div>

      <div className="pb-bp-block">
        <p className="pb-bp-label"><span>F</span> Visual design language</p>
        <p className="pb-bp-text">{visual.text}</p>
        <ul className="pb-bp-swatches">
          {visual.swatches.map(([name, hex]) => (
            <li key={name}>
              <span style={{ background: hex }} />
              {name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Builds the ordered section list (and sidebar index) for a project.
// Feature pillars sit just before Outcome, as in the design.
function buildSections(project, brief, gallery) {
  const sections = [{ id: 'overview', label: 'Overview', type: 'overview' }];
  const pillars = { id: 'feature-pillars', label: 'Feature pillars', type: 'pillars' };
  const wantPillars = project.capabilities || SHOW_GAPS;
  let pillarsAdded = false;

  project.story.forEach((item) => {
    if (item.label === 'Outcome' && wantPillars) {
      sections.push(pillars);
      pillarsAdded = true;
    }
    sections.push({ id: toId(item.label), label: item.label, type: 'story', item });
  });
  if (wantPillars && !pillarsAdded) sections.push(pillars);
  const blueprintOnPage = project.story.some((item) => item.blueprint);
  if (brief || (SHOW_GAPS && !blueprintOnPage)) sections.push({ id: 'brief', label: BRIEF_CAPTION, type: 'brief' });
  if (gallery.length) sections.push({ id: 'gallery', label: project.galleryLabel || 'Gallery', type: 'gallery' });

  return sections.map((s, i) => ({ ...s, num: pad(i + 1) }));
}

function WorkDetail({ project }) {
  const [open, setOpen] = useState(null);

  const index = PROJECTS.indexOf(project);
  const next = PROJECTS[(index + 1) % PROJECTS.length];

  const brief = project.images.find((im) => im.caption === BRIEF_CAPTION);
  const coverImage = project.images.find((im) => im.src === project.cover);
  const opening = project.opening || { src: project.cover, caption: coverImage ? coverImage.caption : 'Opening view' };
  const gallery = project.images.filter((im) => im !== brief && im !== coverImage);
  const sections = buildSections(project, brief, gallery);

  // Every image on the page, in reading order, so the lightbox can step through them
  const storyImages = project.story.flatMap((s) => (s.image ? [s.image] : s.gallery || []));
  const plates = [opening, ...storyImages, brief, ...gallery].filter(Boolean);
  const openPlate = (image) => () => setOpen(plates.indexOf(image));
  const step = (d) => setOpen((i) => (i + d + plates.length) % plates.length);

  useEffect(() => { window.scrollTo(0, 0); }, [project.slug]);
  const active = useActiveSection(sections.map((s) => s.id), project.slug);

  const hasDot = !project.title.includes('.');

  const renderSection = (section) => {
    const { id, num, label, type, item } = section;

    if (type === 'overview') {
      return (
        <section id={id} key={id} className="pb-case-row">
          <div className="pb-case-caption">
            <p className="pb-case-label">Overview</p>
          </div>
          <p className="pb-case-reading">{project.summary}</p>
        </section>
      );
    }

    if (type === 'story') {
      const wantsImage = item.label !== 'Outcome' && !item.gallery && !item.blueprint;
      return (
        <section id={id} key={id} className="pb-case-panel">
          <div className="pb-case-row">
            <div className="pb-case-caption">
              <p className="pb-case-label">{num} / {label}</p>
              {item.title
                ? <h3 className="pb-case-headline">{item.title}</h3>
                : <Needed>headline</Needed>}
            </div>
            <p className="pb-case-reading">{item.text}</p>
          </div>
          {item.image
            ? <Plate image={item.image} left={item.image.caption} right={item.image.note} onOpen={openPlate(item.image)} />
            : wantsImage && <Needed image>{label.toLowerCase()} image</Needed>}
          {item.gallery && <GalleryGrid images={item.gallery} openPlate={openPlate} />}
          {item.blueprint && <Blueprint data={item.blueprint} />}
        </section>
      );
    }

    if (type === 'pillars') {
      const caps = project.capabilities || [];
      return (
        <section id={id} key={id} className="pb-case-pillars">
          <p className="pb-case-label">
            {num} / {caps.length ? `${NUMBER_WORDS[caps.length] || caps.length} feature pillars` : 'Feature pillars'}
          </p>
          {caps.length ? (
            <ol className="pb-pillars">
              {caps.map(([name, text], i) => (
                <li key={name}>
                  <span className="pb-pillar-num">{pad(i + 1)} /</span>
                  <h4>{name}</h4>
                  <p>{text}</p>
                </li>
              ))}
            </ol>
          ) : <Needed>feature pillars (3–4 short name + description pairs)</Needed>}
        </section>
      );
    }

    if (type === 'brief') {
      return (
        <section id={id} key={id}>
          {brief
            ? <Plate image={brief} left={`${num} / ${BRIEF_CAPTION}`} right={brief.note} onOpen={openPlate(brief)} />
            : <Needed image>product design brief</Needed>}
        </section>
      );
    }

    return (
      <section id={id} key={id} className="pb-case-gallery">
        <p className="pb-case-label">{num} / {label}</p>
        <GalleryGrid images={gallery} openPlate={openPlate} />
      </section>
    );
  };

  return (
    <main className="pb-case fade-in">
      <div className="pb-case-layout">
        <aside className="pb-case-sidebar">
          <Link className="pb-case-back" to="/works">← All work</Link>

          <SectionIndex
            eyebrow={`Project / ${pad(index + 1)}`}
            title={project.title}
            items={sections}
            active={active}
            label="Case study sections"
          />

          <dl className="pb-case-meta">
            {project.facts.map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
            ))}
            {project.link && (
              <div>
                <dt>Live</dt>
                <dd><a href={project.link} target="_blank" rel="noreferrer">{project.link.replace(/^https?:\/\/|\/$/g, '')}</a></dd>
              </div>
            )}
          </dl>
        </aside>

        <div className="pb-case-archive">
          <div className="pb-case-identity">
            <p className="pb-case-label">{project.client} · {project.kind}</p>
            <h1>{project.title}{hasDot && <span>.</span>}</h1>
            <p className="pb-case-tagline">{project.tagline}</p>
          </div>

          <Plate image={opening} left={`01 / ${project.kind}`} right={opening.caption} onOpen={openPlate(opening)} />

          {sections.map(renderSection)}
        </div>
      </div>

      <nav className="pb-case-next">
        <div>
          <p className="pb-case-label">The portfolio</p>
          <Link to="/works" className="pb-case-next-back">← Back to all work</Link>
        </div>
        <div className="pb-case-next-project">
          <p className="pb-case-label">Next project / {next.client}</p>
          <Link to={`/works/${next.slug}`} className="pb-case-next-link">
            {next.title}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M12.6648 12.6648V1H1M12.6648 1L1 12.6648" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Link>
          <p className="pb-case-next-tagline">{next.tagline}</p>
        </div>
      </nav>

      {open !== null && (
        <Lightbox images={plates} index={open} onClose={() => setOpen(null)} onStep={step} />
      )}
    </main>
  );
}

function Work() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  return project ? <WorkDetail project={project} /> : <WorkIndex />;
}

export default Work;
